import { useState } from 'react'
import { Bot, Check, ChevronDown, Search, UserRound, UsersRound } from 'lucide-react'
import { Input } from '@/components/ui/input'
import { cn } from '@/lib/utils'
import AgentWakeSelector from '@/frontend/features/Fde/AgentWakeSelector'
import type { FdeCopy, ProjectTaskAssignee } from '@/frontend/features/Fde/types'

interface Props {
  options: ProjectTaskAssignee[]
  values: string[]
  savedValues: string[]
  notificationValues: string[]
  backlog: boolean
  disabled: boolean
  copy: FdeCopy
  onChange: (values: string[]) => void
  onNotificationChange: (values: string[]) => void
}

const groups = [
  { type: 'agent', icon: Bot, label: 'task_people_agents' },
  { type: 'user', icon: UsersRound, label: 'task_people_stationzero' },
  { type: 'guest', icon: UserRound, label: 'task_people_guests' },
  { type: 'client', icon: UserRound, label: 'task_people_client' },
] as const

type GroupType = (typeof groups)[number]['type']

export default function ProjectTaskParticipants({ options, values, savedValues, notificationValues, backlog, disabled, copy, onChange, onNotificationChange }: Props) {
  const [query, setQuery] = useState('')
  const [openGroups, setOpenGroups] = useState<Set<GroupType>>(() => new Set([
    'agent',
    ...options.filter((option) => values.includes(option.value)).map((option) => option.type),
  ]))
  const normalizedQuery = query.trim().toLocaleLowerCase('pt-BR')

  function toggle(value: string) {
    const option = options.find((entry) => entry.value === value)
    if (option && !values.includes(value)) {
      setOpenGroups((current) => new Set([...current, option.type]))
    }
    onChange(values.includes(value) ? values.filter((entry) => entry !== value) : [...values, value])
  }

  function toggleGroup(type: GroupType) {
    setOpenGroups((current) => {
      const next = new Set(current)
      if (next.has(type)) next.delete(type)
      else next.add(type)
      return next
    })
  }

  const visibleGroups = groups.map((group) => ({
    ...group,
    entries: options.filter((option) => option.type === group.type && matchesQuery(option, normalizedQuery)),
    total: options.filter((option) => option.type === group.type).length,
    selected: options.filter((option) => option.type === group.type && values.includes(option.value)).length,
  })).filter((group) => group.total > 0 && (normalizedQuery === '' || group.entries.length > 0))
  const newAgents = options.filter((option) => (
    option.type === 'agent' && values.includes(option.value) && !savedValues.includes(option.value)
  ))

  return <div className="space-y-3">
    <label className="relative block">
      <Search className="pointer-events-none absolute left-2.5 top-1/2 size-3.5 -translate-y-1/2 text-muted-foreground" />
      <Input value={query} onChange={(event) => setQuery(event.target.value)} placeholder={copy.management.task_search_participants} className="h-8 bg-background pl-8 text-xs" />
    </label>

    {visibleGroups.length === 0 && <p className="rounded-md border border-dashed border-border px-3 py-6 text-center text-xs text-muted-foreground">{copy.management.task_no_participants}</p>}

    <div className="space-y-1">
      {visibleGroups.map(({ type, icon: Icon, label, entries, total, selected }) => {
        const open = normalizedQuery !== '' || openGroups.has(type)
        const description = type === 'agent'
          ? (backlog ? copy.management.task_backlog_participants_hint : copy.management.task_agent_auto_dispatch)
          : undefined

        return <section key={type} className="overflow-hidden rounded-lg border border-transparent data-[open=true]:border-border" data-open={open}>
          <button type="button" onClick={() => toggleGroup(type)} title={description} aria-expanded={open} className="flex h-9 w-full items-center gap-2 rounded-lg px-2 text-left text-xs font-medium transition-colors hover:bg-muted/60">
            <Icon className="size-3.5 shrink-0 text-muted-foreground" />
            <span className="min-w-0 flex-1 truncate">{copy.management[label]}</span>
            <span className={cn('text-[10px] tabular-nums', selected > 0 ? 'text-primary' : 'text-muted-foreground')}>
              {copy.management.task_selected_participants.replace('%{selected}', String(selected)).replace('%{total}', String(total))}
            </span>
            <ChevronDown className={cn('size-3.5 shrink-0 text-muted-foreground transition-transform', open && 'rotate-180')} />
          </button>

          {open && <div className="border-t border-border/70 p-1">
            {entries.map((option) => <ParticipantRow key={option.value} option={option} selected={values.includes(option.value)} disabled={disabled} copy={copy} onToggle={() => toggle(option.value)} />)}
          </div>}
        </section>
      })}
    </div>
    {backlog && newAgents.length > 0 && <p className="text-xs text-muted-foreground">{copy.management.task_backlog_participants_hint}</p>}
    {!backlog && newAgents.length > 0 && <AgentWakeSelector
      agents={newAgents}
      values={notificationValues}
      label={copy.management.task_notify_on_save}
      hint={copy.management.task_notify_on_save_hint}
      emptyLabel={copy.management.task_notify_on_save_empty}
      copy={copy.management}
      disabled={disabled}
      onChange={onNotificationChange}
    />}
  </div>
}

function ParticipantRow({ option, selected, disabled, copy, onToggle }: { option: ProjectTaskAssignee; selected: boolean; disabled: boolean; copy: FdeCopy; onToggle: () => void }) {
  const Icon = option.type === 'agent' ? Bot : option.type === 'user' ? UsersRound : UserRound
  const workLabel = option.type === 'agent' && option.work_state ? copy.management.task_work_states[option.work_state] : null

  return <button type="button" aria-label={`${option.name} · ${option.role}`} aria-pressed={selected} disabled={disabled} onClick={onToggle} className={cn('flex h-11 w-full items-center gap-2.5 rounded-md px-2 text-left transition-colors', selected ? 'bg-primary/[0.07]' : 'hover:bg-muted/50', disabled && 'cursor-default')}>
    <span className={cn('flex size-7 shrink-0 items-center justify-center overflow-hidden rounded-full bg-muted text-muted-foreground', option.work_state === 'working' && 'ring-2 ring-primary/50')}>{option.avatar_url ? <img src={option.avatar_url} alt="" className="size-full object-cover" /> : <Icon className="size-3.5" />}</span>
    <span className="min-w-0 flex-1"><span className="flex items-center gap-1.5"><span className="truncate text-xs font-medium text-foreground">{option.name}</span>{workLabel && <span className={cn('shrink-0 text-[9px] font-medium', option.work_state === 'working' ? 'text-primary' : 'text-muted-foreground')}>{workLabel}</span>}</span><span className="block truncate text-[10px] leading-4 text-muted-foreground">{option.role}</span></span>
    <span className={cn('flex size-4.5 shrink-0 items-center justify-center rounded border', selected ? 'border-primary bg-primary text-primary-foreground' : 'border-input bg-background')}><Check className={cn('size-2.5', !selected && 'opacity-0')} /></span>
  </button>
}

function matchesQuery(option: ProjectTaskAssignee, query: string) {
  return query === '' || `${option.name} ${option.role}`.toLocaleLowerCase('pt-BR').includes(query)
}
